/** Просмотрщик медиа игры в агрегированном отчёте ОО (23.08, Саша → 10.10, владелец).
 *  Фото/видео/аудио прямо в дашборде; под ним карточка: кто и когда залил, кто правил,
 *  сколько раз открыли игроки. С правом правки (офис, DC/EO, оператор этой игры): фото —
 *  повернуть, обрезать, «Сохранить», вернуть оригинал; любое медиа — удалить с игры
 *  (второй тап подтверждает). Фото показывается целиком: рамка <img> = само фото, без
 *  object-fit, — по ней же рисуется рамка обрезки. Закрытие — ✕, Esc или тап мимо
 *  (пока есть несохранённая правка — только ✕). */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { api } from "../../api/client";
import type { OoMaterial } from "../../hooks/useOoReports";
import { usePhotoEdit } from "./photoEdit";
import css from "./dashboard.module.css";

interface MaterialInfo {
  id: number; kind: string; label: string;
  uploader: string | null; uploadedAt: string | null;
  editor: string | null; editedAt: string | null;
  views: { total: number; viewers: number; last: string | null; since: string };
  canEdit: boolean;
}
export interface MaterialUrls { poster: string | null; stream: string | null }

const p2 = (n: number): string => String(n).padStart(2, "0");
/** «03.10 13:21» по Оренбургу (UTC+5); год — только если не текущий. */
function fmtWhen(ts: string | null): string {
  if (!ts) return "";
  const t = Date.parse(ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t + 5 * 3_600_000);
  const y = d.getUTCFullYear() !== new Date(Date.now() + 5 * 3_600_000).getUTCFullYear() ? `.${d.getUTCFullYear()}` : "";
  return `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}${y} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}
const fmtDay = (ymd: string): string => (/^\d{4}-\d{2}-\d{2}/.test(ymd) ? `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}` : ymd);
const timesWord = (n: number): string => {
  const m10 = n % 10, m100 = n % 100;
  return m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? "раза" : "раз";
};

function ViewsLine({ kind, v }: { kind: string; v: MaterialInfo["views"] }): ReactElement {
  const verb = kind === "video" ? "Смотрели" : "Открывали";
  return (
    <div>
      {v.total > 0
        ? <>{verb} игроки: <b>{v.total} {timesWord(v.total)}</b> · {v.viewers} чел.{v.last ? ` · последний ${fmtWhen(v.last)}` : ""}</>
        : <>Игроки ещё не {kind === "video" ? "смотрели" : "открывали"}</>}
      <span className={css.mvMuted}> · счёт с {fmtDay(v.since)}</span>
    </div>
  );
}

export function MediaViewerOo({ m, bookingId, onClose, onRemoved, onReplaced }: {
  m: OoMaterial; bookingId: number; onClose: () => void;
  onRemoved: (id: number) => void; onReplaced: (id: number, urls: MaterialUrls) => void;
}): ReactElement {
  const isPhoto = m.kind === "photo";
  const canTransform = isPhoto && !!m.stream; /* превью-тумбу не правим — только сам файл */
  const ed = usePhotoEdit(isPhoto ? m.stream ?? m.poster : null);
  const [info, setInfo] = useState<MaterialInfo | null>(null);
  const [infoErr, setInfoErr] = useState("");
  const [busy, setBusy] = useState<"" | "save" | "restore" | "delete">("");
  const [err, setErr] = useState("");
  const [delArm, setDelArm] = useState(false);

  const loadInfo = useCallback(() => {
    let alive = true;
    api<MaterialInfo>(`/api/game-reports/${bookingId}/material/${m.id}/info`)
      .then((x) => { if (alive) { setInfo(x); setInfoErr(""); } })
      .catch((e: unknown) => { if (alive) setInfoErr(e instanceof Error ? e.message : "нет данных"); });
    return () => { alive = false; };
  }, [bookingId, m.id]);
  useEffect(() => loadInfo(), [loadInfo]);

  const { cropMode, cancelCrop } = ed;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (cropMode) cancelCrop(); else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cropMode, cancelCrop, onClose]);
  useEffect(() => {
    if (!delArm) return;
    const t = setTimeout(() => setDelArm(false), 4000);
    return () => clearTimeout(t);
  }, [delArm]);

  const fail = (e: unknown, fallback: string) => setErr(e instanceof Error && e.message ? e.message : fallback);
  const save = async () => {
    if (!ed.edited || busy) return;
    setBusy("save"); setErr("");
    try {
      const file = await ed.takeFile(`material-${m.id}-edit.jpg`);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("op", ed.ops.join(" + "));
        const r = await api<MaterialUrls>(`/api/game-reports/${bookingId}/material/${m.id}/file`, { method: "PUT", body: fd, retries: 0 });
        onReplaced(m.id, { poster: r.poster, stream: r.stream });
        loadInfo();
      }
    } catch (e) { fail(e, "Не сохранилось — попробуй ещё раз"); }
    setBusy("");
  };
  const restore = async () => {
    if (busy) return;
    setBusy("restore"); setErr("");
    try {
      const r = await api<MaterialUrls>(`/api/game-reports/${bookingId}/material/${m.id}/restore`, { method: "POST", retries: 0 });
      onReplaced(m.id, { poster: r.poster, stream: r.stream });
      loadInfo();
    } catch (e) { fail(e, "Не получилось вернуть оригинал"); }
    setBusy("");
  };
  const remove = async () => {
    if (busy) return;
    if (!delArm) { setDelArm(true); return; }
    setBusy("delete"); setErr("");
    try {
      await api(`/api/game-reports/${bookingId}/material/${m.id}`, { method: "DELETE", retries: 0 });
      onRemoved(m.id);
    } catch (e) { fail(e, "Не удалилось — попробуй ещё раз"); setBusy(""); setDelArm(false); }
  };

  const kindTitle = m.kind === "video" ? `видео${m.duration ? ` · ${m.duration}` : ""}` : m.kind === "photo" ? "фото" : m.kind === "audio" ? "аудио" : m.kind;
  const locked = ed.cropMode || ed.edited || busy !== "";
  return (
    <div className={css.mvOverlay} role="dialog" aria-modal="true" onClick={() => { if (!locked) onClose(); }}>
      <div className={`${css.mvFrame} ${css.mvFrameMat}`} onClick={(e) => e.stopPropagation()}>
        <div className={css.mvHead}>
          <span className={css.mvTitle}>{info?.label ? `${kindTitle} · ${info.label}` : kindTitle}</span>
          <button type="button" className={css.mvClose} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        <div className={css.mvStage}>
          {m.kind === "video" ? <video className={css.mvVideo} controls autoPlay playsInline poster={m.poster ?? undefined} src={m.stream ?? undefined} />
            : m.kind === "audio" ? <audio className={css.mvAudio} controls autoPlay src={m.stream ?? undefined} />
            : ed.shown ? (
              <span className={css.mvImgWrap} data-crop={ed.cropMode ? "" : undefined} {...ed.pointer}>
                <img ref={ed.imgRef} className={css.mvImg} src={ed.shown} alt="" draggable={false} />
                {ed.cropMode && ed.sel ? <span className={css.wvSel} style={{ left: ed.sel.x, top: ed.sel.y, width: ed.sel.w, height: ed.sel.h }} /> : null}
              </span>
            ) : <div className={css.mvEmpty}>файла нет</div>}
        </div>
        <div className={css.mvInfo}>
          {info ? (
            <>
              <div>Залил: <b>{info.uploader ?? "—"}</b>{info.uploadedAt ? ` · ${fmtWhen(info.uploadedAt)}` : ""}</div>
              {info.editedAt ? <div>Правил: <b>{info.editor ?? "—"}</b> · {fmtWhen(info.editedAt)}</div> : null}
              <ViewsLine kind={m.kind} v={info.views} />
            </>
          ) : <div className={css.mvMuted}>{infoErr || "загружаю…"}</div>}
        </div>
        {info?.canEdit ? (
          <div className={`${css.wvBar} ${css.mvBar}`}>
            {canTransform ? (
              <>
                <button type="button" className={css.wvBtn} onClick={ed.rotate} disabled={ed.cropMode || ed.busy || busy !== ""}>⟳ Повернуть</button>
                {ed.cropMode
                  ? <>
                      <button type="button" className={`${css.wvBtn} ${css.wvOk}`} onClick={ed.applyCrop} disabled={!ed.sel || ed.sel.w < 8 || ed.sel.h < 8}>✂ Применить</button>
                      <button type="button" className={css.wvBtn} onClick={ed.cancelCrop}>Отмена</button>
                    </>
                  : <button type="button" className={css.wvBtn} onClick={ed.startCrop} disabled={ed.busy || busy !== ""}>✂ Обрезать</button>}
                {ed.edited
                  ? <>
                      <button type="button" className={`${css.wvBtn} ${css.wvSave}`} onClick={() => void save()} disabled={busy !== "" || ed.busy || ed.cropMode}>{busy === "save" ? "…" : "Сохранить"}</button>
                      <button type="button" className={css.wvBtn} onClick={ed.discard} disabled={busy !== "" || ed.cropMode}>Отменить правку</button>
                    </>
                  : info.editedAt
                    ? <button type="button" className={css.wvBtn} onClick={() => void restore()} disabled={busy !== "" || ed.cropMode}>{busy === "restore" ? "…" : "↺ Вернуть оригинал"}</button>
                    : null}
              </>
            ) : null}
            <button type="button" className={`${css.wvBtn} ${delArm ? css.wvDel : ""}`} onClick={() => void remove()} disabled={busy !== "" || ed.cropMode || ed.edited}>
              {busy === "delete" ? "…" : delArm ? "Точно удалить?" : "✕ Удалить"}
            </button>
          </div>
        ) : null}
        {ed.cropMode ? <div className={css.mvHint}>Проведи по фото рамку и нажми «Применить»</div> : null}
        {err || ed.err ? <div className={css.mvErr}>{err || ed.err}</div> : null}
      </div>
    </div>
  );
}
